import type { PlanDetailsCopy } from '../planDetails'

const ko: PlanDetailsCopy = {
  fieldLabels: {
    current_age: '고객님의 나이',
    retirement_age: '은퇴 시점',
    annual_income: '연 소득',
    annual_savings: '연간 저축액',
    inflation: '물가상승률',
    realization_tax_rate: '양도소득세율',
    cost_basis: '취득가액',
    w0: '현재 투자 가능 자산',
    primary_residence: '거주 주택',
    total_investable: '투자 가능 자산',
    expected_return: '기대수익률',
    volatility: '변동성',
    horizon: '계획 기간',
    jurisdiction: '세법상 거주지',
    risk_aversion: '위험 감수 성향',
    birth_year: '출생 연도',
    birth_date: '생년월일',
    marital_status: '혼인 상태',
    dependents: '부양가족',
    dependent_ages: '부양가족 나이',
    children: '자녀',
    children_birth_years: '자녀 출생 연도',
    partner_age: '배우자 나이',
    partner_annual_income: '배우자 연 소득',
    partner_retirement_age: '배우자 은퇴 나이',
    plan_to_age: '계획 종료 나이',
    gross_annual_income: '세전 연 소득',
    net_income: '실수령 소득',
    annual_spending: '연간 지출',
    essential_spending: '필수 지출',
    flexible_spending: '재량 지출',
    investments_total: '총 투자액',
    total_investable_wealth: '투자 가능 자산 합계',
    portfolio_mix: '포트폴리오 구성',
    cgt_rate: '금융소득 세율',
    interest_rate: '금리',
    dividend_yield: '배당수익률',
    wealth_tax_rate: '부유세율',
    filing_jurisdictions: '세금 신고 국가',
    citizenship: '국적',
    tax_residency: '신고한 세법상 거주지',
    state: '주 또는 도',
    region: '지역',
    city: '도시',
    locality: '시·군·구',
    church_tax_applicable: '교회세 과세 대상',
    life_cover: '사망 보장액',
    income_protection: '소득 보장 보험',
    employer_match: '고용주 매칭 기여금',
    pension: '연금',
    state_pension: '공적 연금',
    social_security: '사회보장',
    wage_growth: '임금 상승률',
    stability: '소득 안정성',
    has_will: '유언장 작성 여부',
    risk_attitude: '위험에 대한 태도',
  },
  keyLabels: {
    goal_priority: '목표 우선순위',
    current_age: '나이',
    inflation: '물가상승률 가정',
    jurisdiction: '세법상 거주지',
    realization_tax_rate: '목표 자금 마련을 위한 매도 시 세금',
    citizenship: '국적',
    total_investable: '투자 가능 자산',
    annual_income: '연 소득',
    annual_savings: '연간 저축액',
    risk_tier: '위험 수준',
    retirement_age: '은퇴 나이',
    target_wealth: '목표 자산',
    primary_residence_sellable: '주택 매도 / 규모 축소',
  },
  channelLabels: {
    web_levers: '웹 조정 항목',
    web_cockpit: '의사결정 패널',
    web: '웹사이트',
    chat: 'AI 어시스턴트',
    mcp: 'AI 어시스턴트',
    full_review: '전체 검토',
    full_plan: '전체 플랜',
    what_if: '시나리오 분석',
  },
  objectiveLabels: {
    status_quo: '현재 상태 유지',
    max_goal_probability: '성공 확률 최대화',
    lowest_drawdown: '최대 낙폭 최소화',
    highest_growth: '성장 최대화',
    fastest_to_goal: '목표 최단 달성',
  },
  choices: {
    interest_only: '이자만 상환',
    fixed_rate_amortizing: '원리금 상환(고정금리)',
    variable_rate_amortizing: '원리금 상환(변동금리)',
    repayment: '원리금 상환',
    refinance: '시장 금리로 재융자',
    repay: '전액 상환',
  },
  values: {
    yearsShort: (n) => `${n}년`,
    shareAbout: (pct) => `가치의 약 ${pct}`,
    sharePart: '가치의 일부',
    aroundAge: (age) => ` (${age}세 무렵)`,
    inAboutYears: (n) => ` (약 ${n}년 후)`,
    maySellFor: (share, when) => `플랜이 개선되는 경우에만 매도하여 ${share}${when}를 확보할 수 있습니다.`,
    maySell: '플랜이 개선되는 경우에만 매도할 수 있습니다.',
    structuredFact: '구조화된 정보',
  },
  ask: { talkThrough: 'MyCIO와 자세히 이야기하기' },
  review: {
    openInputCheckFailed: '미입력 항목 점검을 완료하지 못했습니다. 아래 검토 상태를 확인하세요.',
    finishedLoading: '검토가 완료되었습니다. 결과를 불러오는 중…',
    draftReady: '잠정 검토가 준비되었습니다. 완료된 분석, 계산하지 못한 부분, 이를 채우는 데 필요한 질문이 아래에 있습니다.',
    finishedNotLoaded: '검토는 완료되었지만 아직 결과를 불러오지 못했습니다. 다시 시도하는 중이며, 계산을 다시 실행하지는 않습니다.',
    fetchFailed: '검토는 완료되었지만 지금은 가져올 수 없습니다. 결과는 저장되어 있으니 잠시 후 다시 시도하세요. 다시 계산할 필요는 없습니다.',
    notComputed: (list) => `이번 실행에서 계산되지 않음: ${list}.`,
    changesNotSaved: (_n, f) => `변경 사항 ${f}건이 저장되지 않았습니다. '내 정보'를 확인하세요.`,
  },
  facts: {
    chooseOption: '옵션을 선택하세요.',
    couldNotSave: '값을 저장하지 못했습니다. 플랜은 변경되지 않았습니다.',
    couldNotUndo: '변경 사항을 되돌리지 못했습니다.',
    choosePlaceholder: '선택…',
    confirmedBadge: '확인됨 — 변경은 채팅으로 요청하세요',
    confirmedTitle: (was) => `이 항목은 이전에 ${was}(으)로 확인하셨습니다. 바꾸시려면 채팅에서 MyCIO에게 요청하세요.`,
  },
  assumptionsToConfirm: { anInput: '어떤 입력 항목', assumedSuffix: (v) => ` — 가정값: ${v}` },
  editor: {
    enterNumber: '숫자를 입력하세요.',
    enterNumberFor: (l) => `'${l}'에 숫자를 입력하세요.`,
    enterPercentFor: (l) => `'${l}'에 백분율을 입력하세요.`,
    chooseYesNoFor: (l) => `'${l}'에 대해 예 또는 아니요를 선택하세요.`,
    semicolonBetween: (l) => `'${l}'의 항목은 ";"로 구분하세요.`,
    useNumbersFor: (l) => `'${l}'에는 숫자를 사용하세요.`,
    wholeNumbersFor: (l) => `'${l}'에는 정수를 사용하고 ";"로 구분하세요.`,
    commaSeparated: '쉼표로 구분',
  },
  freshnessUnknown: '저장된 계산에 최근 계좌 변경 사항이 반영되었는지 확인하지 못했습니다. 이 수치를 근거로 판단하시기 전에 페이지를 새로 고치고 계좌를 확인하세요.',
  goals: { goalN: (n) => `목표 ${n}`, whatMoves: '영향을 주는 요인 →', earliest: (y) => `가장 이르면 ≈ ${y}`, rank: '순위' },
  issues: {
    mainConstraint: '이 플랜의 주요 제약',
    riskToReview: '검토할 위험',
    missingInformation: '부족한 정보',
    whyItMatters: (t) => `중요한 이유: ${t}`,
    seeEvidence: '근거 보기 ↓',
    noneFound: '완료된 분석에서는 현재 이 플랜에 있는 정보로 중요한 문제를 찾지 못했습니다.',
    notAvailable: '문제 분석을 아직 이용할 수 없습니다. 이 섹션이 비어 있다고 해서 문제가 없다는 뜻은 아닙니다.',
    discussQuestion: '이 플랜에서 찾은 문제들을 검토하고 각각의 근거를 설명해 주세요. 그리고 제 상황에 대해 빠진 정보가 우선순위나 결론을 바꿀 수 있는지 물어봐 주세요.',
    discussLabel: '이 문제들에 대해 이야기하기',
  },
  policies: {
    optionTested: '검증한 옵션',
    computedEffect: (v) => `계산된 효과: ${v}`,
    tradeoff: (v) => `절충: ${v}`,
    backToAction: '실행 항목으로 돌아가기 ↑',
    tradeoffsHeading: '검증한 사례의 절충점',
  },
  transfer: {
    toHeirs: (m) => `상속인에게 +${m}`,
    fundedPoints: (label, pts) => `${label} ${pts}%p`,
    noChange: ' · 목표 자금 조달에 변화 없음',
  },
  tax: {
    rulesIntro: '이 계산에 연결된 규정만 표시합니다. 각 항목에는 기록된 출처 정보가 표시되며, 날짜나 적용 범위 정보가 없으면 없다고 표시됩니다.',
    title: '이 플랜에 적용된 세무 처리',
    countryBookPrimary: '예측에는 해당 국가의 세법이 연도별로 적용되었습니다. 공식 또는 1차 자료로 명시적으로 기록된 출처는 아래에 있습니다.',
    countryBookNoPrimary: '예측에는 해당 국가의 세법이 연도별로 적용되었습니다. 이 저장된 결과에는 아직 1차 자료 링크가 첨부되지 않았으므로, 세금에 민감한 결정은 실행 전에 확인하세요.',
    simplified: '이 계산에는 단순화된 세무 처리가 사용되었습니다. 세금에 민감한 비교에 의존하기 전에 아래 한계를 검토하세요.',
    askQuestion: '이 플랜에 어떤 세법과 국가별 층위가 적용되었는지, 무엇이 근사되거나 제외되었는지, 그리고 제 세무 상황의 어떤 사실이 결과를 바꿀 수 있는지 설명해 주세요. 이 계산에 실제로 첨부된 1차 자료만 사용하고 빠진 출처가 있으면 알려 주세요.',
    askLabel: '세무 처리에 대해 묻기',
  },
  inputs: { title: '결과를 바꿀 수 있는 입력 항목', infoToConfirm: '확인이 필요한 정보' },
  gated: {
    countChoices: (_n, f) => `고객님을 위해 계산한 옵션 ${f}개`,
    mappedYears: (_n, f) => `향후 ${f}년을 계산했습니다`,
    extraChecks: (_n, f) => `플랜을 ${f}가지 방법으로 추가 점검했습니다`,
    placeholderRows: ['예시 행', '예시 수치', '예시 옵션', '자리 표시자'],
  },
}

export default ko
